import { Button } from '@creditornot/cb-button';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import React, { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { VenueIsLinkedAlert } from 'modules/menu/components/VenueIsLinkedAlert';
import { useSharedMenuLink } from 'modules/menu/hooks/useSharedMenuLink';

import { CategoryInputFields } from './CategoryInputFields';
import { CreateCategoryFormValues } from './types';
import { categoriesValidationSchema } from './validationSchema';

type AddCategoryFormSectionProps = {
  onCancelClick: () => void;
  onNextClick: () => void;
  onCloseClick: () => void;
  className?: string;
};

const CreateCategoryFormFooter = styled(ModalFooter)``;

const CancelButton = styled(Button)`
  margin-inline-start: auto;
`;

const NextButton = styled(Button)`
  margin-inline-end: 72px;
  margin-inline-start: 16px;
`;

const AddCategoryFormSectionBody = styled(ModalContent)`
  flex: 1;
`;

const CategoriesFields = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;

const AddCategoryFormSectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const AddCategoryFormSection = ({
  onCancelClick,
  onNextClick,
  onCloseClick,
  className,
}: AddCategoryFormSectionProps) => {
  const { values } = useFormState<CreateCategoryFormValues>();
  const isFormSectionInvalid = useMemo(
    () => !categoriesValidationSchema.isValidSync(values.categories),
    [values.categories],
  );
  const { sharedMenuLink } = useSharedMenuLink();

  return (
    <AddCategoryFormSectionRoot className={className}>
      <ModalHeader onCloseClick={onCloseClick}>
        <FormattedMessage id="menu.bulkCreateCategories.addCategory.title" />
      </ModalHeader>
      {!!sharedMenuLink && sharedMenuLink.selection_is_shared && <VenueIsLinkedAlert />}
      <AddCategoryFormSectionBody>
        <CategoriesFields>
          <CategoryInputFields />
          <InputAlert variant="info">
            <FormattedMessage id="menu.bulkCreateCategories.addCategory.info" />
          </InputAlert>
        </CategoriesFields>
      </AddCategoryFormSectionBody>

      <CreateCategoryFormFooter>
        <CancelButton variant="lightBlue" size="small" onClick={onCancelClick}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <NextButton variant="blue" size="small" disabled={isFormSectionInvalid} onClick={onNextClick}>
          <FormattedMessage id="menu.bulkCreateCategories.addCategory.next" />
        </NextButton>
      </CreateCategoryFormFooter>
    </AddCategoryFormSectionRoot>
  );
};
